import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  IoListOutline,
  IoAdd,
  IoTrashOutline,
  IoWarningOutline,
  IoSwapHorizontalOutline,
  IoPencil,
  IoImageOutline,
  IoPersonCircle,
  IoCheckboxOutline,
  IoCheckmarkDone,
  IoClose,
} from "react-icons/io5";
import api from "@/api";
import { useI18n, ALL_STORES, STORE_LABELS } from "@/i18n";
import { AppHeader } from "@/components/AppHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { relativeTime, formatDateTimeIT } from "@/utils";
import { deleteWithUndo } from "@/utils/undoToast";

const ENTITY_TYPES = ["product", "problem", "expiry"];

const ACTION_STYLE = {
  created: { cls: "green", Icon: IoAdd },
  updated: { cls: "yellow", Icon: IoPencil },
  deleted: { cls: "red", Icon: IoTrashOutline },
  transferred: { cls: "blue", Icon: IoSwapHorizontalOutline },
  moved_to_expired: { cls: "red", Icon: IoWarningOutline },
  photo_added: { cls: "gray", Icon: IoImageOutline },
};

export default function HistoryPage() {
  const { t, lang } = useI18n();
  const [store, setStore] = useState("");
  const [entity, setEntity] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (store) params.store_id = store;
      if (entity) params.entity_type = entity;
      const { data } = await api.get("/history", { params });
      setItems((data || []).filter((h) => h.entity_type !== "auth"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [store, entity]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const idToDelete = pendingDelete;
    const snapshot = items.find((h) => h.id === idToDelete);
    // Optimistic remove
    setItems((prev) => prev.filter((h) => h.id !== idToDelete));
    setPendingDelete(null);
    let cancelled = false;
    deleteWithUndo({
      label: "Voce eliminata",
      duration: 5000,
      onCommit: async () => {
        if (cancelled) return;
        try {
          await api.delete(`/history/${idToDelete}`);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Errore");
          load();
        }
      },
      onUndo: () => {
        cancelled = true;
        if (snapshot) setItems((prev) => [snapshot, ...prev]);
      },
    });
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const startSelectMode = () => { setSelectMode(true); setSelected(new Set()); };
  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };
  const selectAllToggle = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((h) => h.id)));
  };

  const doBulkDelete = async () => {
    if (selected.size === 0) return;
    const idsSnapshot = [...selected];
    // Optimistically remove from UI so the list refresh feels instant on slow networks.
    setItems((prev) => prev.filter((h) => !selected.has(h.id)));
    setConfirmBulkOpen(false);
    exitSelectMode();
    try {
      const { data } = await api.post("/history/bulk_delete", { ids: idsSnapshot });
      toast.success(`Eliminate ${data.deleted} voci`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
      load(); // resync
    }
  };

  const buildName = (h) => {
    const d = h.details || {};
    return d.name || d.product_name || d.title || t(`entity_${h.entity_type}`);
  };

  const buildQuantity = (h) => {
    const d = h.details || {};
    if (typeof d.quantity === "number") return d.quantity;
    return null;
  };

  const buildSubline = (h) => {
    const storeLabel = h.store_id ? t(STORE_LABELS[h.store_id]) : "";
    const d = h.details || {};
    switch (h.action) {
      case "created":
        return storeLabel ? `${t("history_added_to")} ${storeLabel}` : "";
      case "deleted":
        return storeLabel ? `${t("history_removed_from")} ${storeLabel}` : "";
      case "moved_to_expired":
        return storeLabel ? `${t("history_removed_from")} ${storeLabel}` : "";
      case "transferred": {
        const to = d.to_store ? t(STORE_LABELS[d.to_store]) : "";
        if (storeLabel && to) return t("history_from_to").replace("$", storeLabel).replace("$", to);
        return storeLabel || "";
      }
      case "updated":
        return storeLabel ? `${t("history_updated_in")} ${storeLabel}` : "";
      default:
        return storeLabel || "";
    }
  };

  const withBottomBar = selectMode && selected.size > 0;

  return (
    <div className={`mobile-frame ${withBottomBar ? "with-bottom-bar" : ""}`} data-testid="history-page">
      {selectMode ? (
        <div className="tf-header-simple">
          <button data-testid="history-select-cancel" className="tf-close-btn" onClick={exitSelectMode} aria-label={t("cancel")}>
            <IoClose size={22} />
          </button>
          <div className="tf-header-title-center" data-testid="page-title">
            {selected.size === 0
              ? t("select_all")
              : selected.size === 1
              ? "1 selezionato"
              : `${selected.size} selezionati`}
          </div>
          <button data-testid="history-select-all-btn" className="tf-icon-btn ghost" onClick={selectAllToggle} aria-label={t("select_all")}>
            <IoCheckmarkDone size={20} color="#E2001A" />
          </button>
        </div>
      ) : (
        <AppHeader
          title={t("history")}
          showBack
          backTo="/"
          right={
            items.length > 0 ? (
              <button
                data-testid="history-select-btn"
                onClick={startSelectMode}
                className="tf-icon-btn ghost"
                aria-label={t("select_all")}
              >
                <IoCheckboxOutline size={20} />
              </button>
            ) : null
          }
        />
      )}
      <div className="tf-content">
        {!selectMode && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            <select data-testid="history-store" className="tf-select" value={store} onChange={(e) => setStore(e.target.value)}>
              <option value="">{t("all_stores")}</option>
              {ALL_STORES.map((s) => (<option key={s} value={s}>{t(STORE_LABELS[s])}</option>))}
            </select>
            <select data-testid="history-entity" className="tf-select" value={entity} onChange={(e) => setEntity(e.target.value)}>
              <option value="">{t("entity")}</option>
              {ENTITY_TYPES.map((e) => (
                <option key={e} value={e}>{t(`entity_${e}`)}</option>
              ))}
            </select>
          </div>
        )}

        {loading ? (
          <div className="tf-empty">{t("loading")}</div>
        ) : items.length === 0 ? (
          <div className="tf-empty">
            <IoListOutline size={44} />
            <div>{t("no_history")}</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {items.map((h) => {
              const style = ACTION_STYLE[h.action] || { cls: "gray", Icon: IoListOutline };
              const Icon = style.Icon;
              const label = t(`history_label_${h.action}`) || h.action.toUpperCase();
              const qty = buildQuantity(h);
              const isSelected = selected.has(h.id);
              return (
                <div
                  key={h.id}
                  data-testid={`history-item-${h.id}`}
                  className={`tf-history-item ${selectMode && isSelected ? "selected" : ""}`}
                  onClick={selectMode ? () => toggleSelect(h.id) : undefined}
                  style={selectMode ? { cursor: "pointer" } : undefined}
                >
                  <div className={`tf-history-icon ${style.cls}`} style={{ position: "relative" }}>
                    <Icon size={22} />
                    {selectMode && isSelected && (
                      <span
                        style={{
                          position: "absolute", top: -6, right: -6,
                          width: 22, height: 22, borderRadius: 999,
                          background: "var(--tf-red)", color: "white",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          border: "2px solid var(--tf-surface)",
                        }}
                      >
                        <IoCheckmarkDone size={14} />
                      </span>
                    )}
                  </div>
                  <div className="tf-history-body">
                    <div className="tf-between" style={{ alignItems: "flex-start", gap: 12 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="tf-history-action-label">{label}</div>
                        <div className="tf-history-name">{buildName(h)}</div>
                        <div className="tf-history-sub">{buildSubline(h)}</div>
                      </div>
                      {qty != null && (
                        <div className="tf-history-qty" data-testid={`history-qty-${h.id}`}>
                          {qty} {t("pcs")}
                        </div>
                      )}
                    </div>
                    <div className="tf-history-footer">
                      <span className="tf-history-user-pill">
                        <IoPersonCircle size={16} />
                        {t("history_by")} {h.user_name || "—"}
                      </span>
                      <span
                        className="tf-history-time"
                        data-testid={`history-time-${h.id}`}
                        title={formatDateTimeIT(h.created_at)}
                      >
                        {relativeTime(h.created_at, lang)}
                      </span>
                      {!selectMode && (
                        <button
                          type="button"
                          className="tf-history-delete-btn"
                          data-testid={`history-delete-${h.id}`}
                          onClick={(e) => { e.stopPropagation(); setPendingDelete(h.id); }}
                          aria-label="Elimina voce"
                          title="Elimina voce"
                        >
                          <IoTrashOutline size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {withBottomBar && (
        <div className="tf-select-bottom-bar" data-testid="history-select-bottom-bar">
          <button
            data-testid="history-bulk-delete-btn"
            onClick={() => setConfirmBulkOpen(true)}
          >
            <IoTrashOutline size={22} />
            {t("delete")} ({selected.size})
          </button>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(v) => !v && setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Eliminare questa voce?"
        description="La voce dello storico verrà rimossa definitivamente."
        confirmLabel="Elimina"
      />
      <ConfirmDialog
        open={confirmBulkOpen}
        onOpenChange={setConfirmBulkOpen}
        onConfirm={doBulkDelete}
        title={`Eliminare ${selected.size} voci?`}
        description="Le voci selezionate verranno rimosse definitivamente dallo storico."
        confirmLabel="Elimina tutte"
      />
    </div>
  );
}
